// ─── Système de ranking Duel beta ─────────────────────────────────────────────
// UNE SEULE monnaie : l'ELO (duel_mmr). Les points affichés = le MMR, les
// divisions = paliers de MMR. duel_reputation est conservée en DB comme MIROIR
// de duel_mmr (compat Lobby/Classement) — voir migration_elo_unification.sql
// pour resynchroniser les anciens joueurs.
//
// Gains/pertes via eloDelta (K=32) : battre plus fort rapporte gros, battre
// plus faible presque rien — le scaling infini s'auto-régule (gains → 0 quand
// on dépasse le pool, une défaite contre le pool coûte ~30).
//
// Thème d'affichage configurable : "classic" (beta) ou "five" (futur).
// Pour basculer vers le thème Five : changer RANK_DISPLAY_THEME = "five".

import { sb } from "../../../lib/supabase";
import type { DuelResult } from "../types/leagueDuel";
import type { OpponentType } from "./duelMatchmaking";
import { eloDelta } from "./elo";

// ── Thème actif ───────────────────────────────────────────────────────────────
// Changer ici pour basculer : "classic" → beta  |  "five" → futur
export type RankDisplayTheme = "classic" | "five";
export const RANK_DISPLAY_THEME: RankDisplayTheme = "classic";

// ── Structure d'un rang ───────────────────────────────────────────────────────
export interface RankDef {
  id: string;
  label: string;
  icon: string;
  minRep: number;
}

// ── Thème Classic — utilisé en beta ──────────────────────────────────────────
// Paliers en points ELO (duel_mmr) : départ START_ELO=100, un palier tous les
// 100 pts, ±16/match équilibré → ~7 victoires nettes par division.
export const CLASSIC_RANKS: RankDef[] = [
  { id: "bronze",   label: "Bronze",   icon: "🥉", minRep: 0   },
  { id: "silver",   label: "Silver",   icon: "🥈", minRep: 100 },
  { id: "gold",     label: "Gold",     icon: "🥇", minRep: 200 },
  { id: "platinum", label: "Platinum", icon: "💿", minRep: 300 },
  { id: "emerald",  label: "Emerald",  icon: "🟢", minRep: 400 },
  { id: "ruby",     label: "Ruby",     icon: "🔴", minRep: 500 },
  { id: "diamond",  label: "Diamond",  icon: "💎", minRep: 600 },
];

// (Le thème "Five" a été retiré — le mode est simplement "Duel" désormais.)

/** Retourne la liste de rangs pour le thème donné. */
export function getRankConfig(_theme: RankDisplayTheme = RANK_DISPLAY_THEME): RankDef[] {
  return CLASSIC_RANKS;
}

/** Rang courant d'un joueur (label affiché). */
export function reputationToRank(
  reputation: number,
  theme: RankDisplayTheme = RANK_DISPLAY_THEME
): string {
  const ranks = getRankConfig(theme);
  let current = ranks[0].label;
  for (const r of ranks) {
    if (reputation >= r.minRep) current = r.label;
    else break;
  }
  return current;
}

/** Progression complète vers le rang suivant. */
export function rankProgress(
  reputation: number,
  theme: RankDisplayTheme = RANK_DISPLAY_THEME
): {
  rank: string;
  rankIcon: string;
  nextRank: string | null;
  nextRankIcon: string | null;
  currentMinRep: number;
  nextMinRep: number | null;
  progressInCurrentRank: number;
  progressNeeded: number | null;
  progressPercent: number;
  // compat legacy
  repIntoRank: number;
  repForNext: number | null;
  ratio: number;
} {
  const ranks = getRankConfig(theme);
  let idx = 0;
  for (let i = 0; i < ranks.length; i++) {
    if (reputation >= ranks[i].minRep) idx = i;
    else break;
  }
  const cur = ranks[idx];
  const next = ranks[idx + 1] ?? null;

  if (!next) {
    return {
      rank: cur.label, rankIcon: cur.icon,
      nextRank: null, nextRankIcon: null,
      currentMinRep: cur.minRep, nextMinRep: null,
      progressInCurrentRank: reputation - cur.minRep,
      progressNeeded: null, progressPercent: 1,
      repIntoRank: reputation - cur.minRep, repForNext: null, ratio: 1,
    };
  }

  const span = next.minRep - cur.minRep;
  const into = reputation - cur.minRep;
  const pct  = span > 0 ? Math.min(1, into / span) : 0;

  return {
    rank: cur.label, rankIcon: cur.icon,
    nextRank: next.label, nextRankIcon: next.icon,
    currentMinRep: cur.minRep, nextMinRep: next.minRep,
    progressInCurrentRank: into, progressNeeded: span, progressPercent: pct,
    repIntoRank: into, repForNext: span, ratio: pct,
  };
}

// ── Résultat du calcul ─────────────────────────────────────────────────────────

const XP_GAINS: Record<DuelResult, number> = {
  win: 30,
  draw: 15,
  loss: 15,
};

export interface DuelRankDelta {
  mmrDelta: number;
  reputationDelta: number;
  mmrBefore: number;
  mmrAfter: number;
  repBefore: number;
  repAfter: number;
  newRank: string;
  xpGain: number;
  xpAfter: number;
}

/**
 * Calcule la variation d'ELO suite à un match. Pur (pas d'effet de bord) —
 * toujours appeler saveDuelResult séparément.
 *
 * Le delta suit la formule ELO sur l'écart de MMR avec l'adversaire : battre
 * plus fort rapporte gros (+29 vs +400), battre plus faible presque rien
 * (+3 vs -400), et inversement en défaite.
 *
 * Les champs rep* sont un MIROIR des champs mmr* : la réputation n'est plus
 * une monnaie séparée, seul l'affichage (duel_reputation en DB, UI existante)
 * en garde le nom.
 */
export function calculateDuelRankDelta(
  mmrBefore: number,
  opponentMmr: number,
  result: DuelResult,
  _opponentType: OpponentType,
  xpBefore: number = 0
): DuelRankDelta {
  const mmrDelta = eloDelta(mmrBefore, opponentMmr, result);
  const xpGain = XP_GAINS[result];
  const mmrAfter = Math.max(0, mmrBefore + mmrDelta);

  console.log(`[Ranking] ELO: ${mmrBefore} vs ${opponentMmr} → mmrDelta ${mmrDelta >= 0 ? "+" : ""}${mmrDelta}`);

  return {
    mmrDelta,
    reputationDelta: mmrDelta,
    mmrBefore,
    mmrAfter,
    repBefore: mmrBefore,
    repAfter: mmrAfter,
    newRank: reputationToRank(mmrAfter),
    xpGain,
    xpAfter: xpBefore + xpGain,
  };
}

// ── Persistance ────────────────────────────────────────────────────────────────

/**
 * Le défenseur offline gagne/perd de l'ELO quand on l'attaque, mais réduit :
 * il encaisse DEFENDER_DELTA_RATIO (50%) de son propre delta ELO. Appliqué à sa
 * ligne players via la RPC apply_duel_defense (SECURITY DEFINER, contourne RLS).
 */
export const AFFECTS_DEFENDER_RANK = true;

/** Fraction du delta ELO propre au défenseur réellement appliquée (gains ET pertes). */
export const DEFENDER_DELTA_RATIO = 0.5;

/** Résultat vu du côté du défenseur (miroir de celui de l'attaquant). */
function invertResult(result: DuelResult): DuelResult {
  return result === "win" ? "loss" : result === "loss" ? "win" : "draw";
}

/**
 * Delta ELO effectif du défenseur : son propre delta ELO (formule K=32 sur
 * l'écart avec l'attaquant, résultat inversé) réduit à DEFENDER_DELTA_RATIO.
 * Retourne 0 si AFFECTS_DEFENDER_RANK est désactivé.
 */
export function calculateDefenderDelta(
  defenderMmr: number,
  attackerMmr: number,
  attackerResult: DuelResult
): number {
  if (!AFFECTS_DEFENDER_RANK) return 0;
  const full = eloDelta(defenderMmr, attackerMmr, invertResult(attackerResult));
  return Math.round(full * DEFENDER_DELTA_RATIO);
}

/**
 * Sauvegarde le résultat en DB.
 * - Met à jour duel_mmr et duel_reputation du joueur.
 * - Insère une ligne dans duel_history (utilisée pour l'écran Defenses).
 *
 * Schema duel_history attendu :
 *   attacker_profile_id uuid nullable,
 *   defender_profile_id uuid nullable,
 *   attacker_name text,   -- snapshot du nom au moment du match
 *   defender_name text,   -- snapshot du nom au moment du match
 *   opponent_type text, result text,
 *   home_score int, away_score int,
 *   rep_delta int, def_rep_delta int,
 *   played_at timestamptz
 */
export async function saveDuelResult(
  playerName: string,
  opponentName: string,
  opponentType: OpponentType,
  result: DuelResult,
  delta: DuelRankDelta,
  homeScore: number = 0,
  awayScore: number = 0,
  attackerProfileId?: string | null,
  defenderProfileId?: string | null,
  attackerAvatar?: string | null,
  defenderMmr?: number
): Promise<void> {
  // Delta ELO du défenseur (50% de son propre delta) — appliqué seulement contre
  // un vrai joueur identifiable (profile_id ou nom), jamais contre un bot.
  const defenderDelta =
    opponentType === "real_user" && typeof defenderMmr === "number"
      ? calculateDefenderDelta(defenderMmr, delta.mmrBefore, result)
      : 0;
  console.log(
    `[Ranking] 📊 Result: ${result} vs ${opponentName} (${opponentType})`
  );
  console.log(
    `[Ranking] MMR: ${delta.mmrBefore} → ${delta.mmrAfter} (${delta.mmrDelta >= 0 ? "+" : ""}${delta.mmrDelta})`
  );
  console.log(
    `[Ranking] Reputation: ${delta.repBefore} → ${delta.repAfter} (${delta.reputationDelta >= 0 ? "+" : ""}${delta.reputationDelta})`
  );
  console.log(
    `[Ranking] XP: +${delta.xpGain} → ${delta.xpAfter}`
  );
  console.log(
    `[Ranking] Rank: ${delta.newRank} | affects_defender_rank: ${AFFECTS_DEFENDER_RANK}`
  );
  console.log(
    `[Ranking] attacker_profile_id: ${attackerProfileId ?? "null"} | defender_profile_id: ${defenderProfileId ?? "null"}`
  );

  const patchOk = await sb.patch(
    "players",
    `name=eq.${encodeURIComponent(playerName)}`,
    {
      duel_mmr: delta.mmrAfter,
      duel_reputation: delta.repAfter,
      duel_rank: delta.newRank,
      duel_xp: delta.xpAfter,
      last_duel_attack_at: new Date().toISOString(),
    }
  );
  if (!patchOk) {
    console.warn("[Ranking] ⚠️ players patch a échoué — MMR/reputation/rank/xp non persistés en DB");
  }

  const historyOk = await sb.insert("duel_history", {
    attacker_profile_id: attackerProfileId ?? null,
    defender_profile_id: defenderProfileId ?? null,
    attacker_name: playerName,
    defender_name: opponentName,
    attacker_avatar: attackerAvatar ?? null,
    opponent_type: opponentType,
    result,
    home_score: homeScore,
    away_score: awayScore,
    rep_delta: delta.reputationDelta,
    def_rep_delta: defenderDelta,
    played_at: new Date().toISOString(),
  });
  if (!historyOk) {
    console.warn("[Ranking] ⚠️ duel_history insert a échoué — la ligne d'historique n'a pas été enregistrée");
  }

  // Applique le delta ELO au défenseur offline (RPC SECURITY DEFINER).
  if (defenderDelta !== 0) {
    console.log(`[Ranking] défenseur ${opponentName} → ELO ${defenderDelta >= 0 ? "+" : ""}${defenderDelta} (50% appliqué via RPC)`);
    try {
      await sb.rpc("apply_duel_defense", {
        p_defender_profile_id: defenderProfileId ?? null,
        p_defender_name: opponentName,
        p_def_delta: defenderDelta,
      });
    } catch (e) {
      console.warn("[Ranking] ⚠️ apply_duel_defense a échoué — ELO défenseur non appliqué", e);
    }
  }
}
